import React, {createContext, useReducer} from 'react';
import CreateSchemaReducer, {ActionType} from './CreateSchemaReducer';
import Notification from "../../../../common/Notification";
import {GenerateSchemaModel} from "../GenerateSchemaStep";
import SomlService from '../../../../services/soml-service';
import {EXISTING_SCHEMA_ID} from "../../../../services/errorcodes";
import {Schema, SchemaResponse} from "../../../../types/Schema";
import OntologyService from "../../../../services/ontology-service";
import * as SomlUtils from "../../../../utils/soml-utils";
import {RbacPermissions} from "../../../../types/Rbac";
import {canCreateSchema, noPermissionMessage} from "../../../../utils/permission-utils";
import {Configurations} from "../../../../types/Configurations";

export type GenerateActionResult = {
  success: boolean;
  hasError: boolean;
}

export type CreateSchemaState = {
  error: any,
  // array which contains metadata for the uploaded ontology files
  files: Array<any>,
  // yaml schema as string
  schema: string;
  schemaId: string;
  // flag indicating that some background process is running - usually an HTTP request
  isRequestRunning: boolean;
  // uploading progress in percents
  progress: number;
  // flag which shows if uploading request is in progress
  uploading: boolean;
  // security config
  permissions?: RbacPermissions
  // warnings returned by the schema generator
  generationWarnings: Array<string>
  // remove schema generation warnings from this context
  removeGenerationWarnings: () => void;
  // execute ontology files upload
  uploadOntologies: (files: Array<File>) => void;
  // delete an ontology from the workbench store on the server
  deleteOntology: (filePath: string) => void;
  // execute schema generation by calling the soaas generator service
  generateSchema: (data: GenerateSchemaModel) => Promise<GenerateActionResult>;
  // save the soml schema in soaas
  saveSchema: (schema: string) => Promise<boolean>;
  // update soml schema in sooas using id
  updateSchema: (schemaId: string, schema: string) => Promise<boolean>;
  // change the schema currently stored in this context
  changeSchema: (schema: string) => void;
  // execute bind schema request in soaas
  activateSchema: (schemaId: string) => Promise<boolean>;
  // remove an error stored in this context
  removeError: () => void;
}

const initialState: CreateSchemaState = {
  error: '',
  files: [],
  schema: '',
  schemaId: '',
  progress: 0,
  uploading: false,
  isRequestRunning: false,
  permissions: undefined,
  generationWarnings: [],
  removeGenerationWarnings: () => {
  },
  uploadOntologies: (files: Array<File>) => {
  },
  deleteOntology: (filePath: string) => {
  },
  generateSchema: async (data: GenerateSchemaModel) => {
    return Promise.resolve({success: true, hasError: false});
  },
  saveSchema: async (schema: string) => {
    return Promise.resolve(false);
  },
  updateSchema: (schemaId: string, schema: string) => {
    return Promise.resolve(false);
  },
  changeSchema: (schema: string) => {
  },
  activateSchema: (schemaId: string) => {
    return Promise.resolve(false);
  },
  removeError: () => {
  }
};

export const CreateSchemaContext = createContext(initialState);

type CreateSchemaContextProviderProps = {
  configurations: Configurations;
  children: any;
}

export const CreateSchemaContextProvider = (props: CreateSchemaContextProviderProps) => {
  const [state, dispatch] = useReducer(CreateSchemaReducer, initialState);

  const reportError = (errorResponse: any, operation: ActionType, skipNotification: boolean = false) => {
    let error = errorResponse;
    if (error.response && error.response.data) {
      error = errorResponse.response.data;
    }
    // json+ld errorResponse
    if (error['@type'] === 'Error') {
      error = [{code: error.code, message: error.message}];
    } else if(error['hydra:member'] && error['hydra:member'].length) {
      error = error['hydra:member'].map((err: any) => {
        return {code: err.code, message: err.message};
      })
    } else if (error._embedded && error._embedded.errorResponseList) {
      error = error._embedded.errorResponseList.map((err: any) => {
        return {code: err.code, message: err.message}
      });
    } else if (error.message) {
      error = {code: error.code || error.name, message: error.message};
    }
    dispatch({
      type: operation,
      payload: {
        error: error
      }
    });
    if (!skipNotification) {
      Notification.error({message: JSON.stringify(error)});
    }
  };

  const toggleRequestIsRunningFlag = (running: boolean) => {
    dispatch({
      type: ActionType.TOGGLE_REQUEST_IN_PROGRESS,
      payload: {
        isRequestRunning: running
      }
    });
  };

  const setSchemaId = () => {
    const schemaId = SomlUtils.extractSchemaId(state.schema);
    dispatch({
      type: ActionType.SET_SCHEMA_ID,
      payload: {
        schemaId
      }
    })
  };

  async function uploadOntologies(files: Array<File>) {
    if (files.length) {
      try {
        const response = await OntologyService.uploadOntologies(files, (progress) => {
          dispatch({
            type: ActionType.ONTOLOGY_UPLOAD_PROGRESS,
            payload: {
              progress: (progress.loaded / progress.total * 100),
              uploading: true
            }
          });
        });
        dispatch({
          type: ActionType.ONTOLOGY_FILES_UPLOADED,
          payload: {
            uploading: false,
            files: response.data
          }
        });
        Notification.success({message: 'Successfully uploaded all selected files'});
      } catch (error) {
        dispatch({
          type: ActionType.ONTOLOGY_UPLOAD_FAIL,
          payload: {
            uploading: false
          }
        });
        Notification.error({message: error.message});
      }
    }
  }

  async function deleteOntology(filePath: string) {
    try {
      await OntologyService.deleteOntoloy(filePath);
      const files = state.files.filter((file: any) => file.path !== filePath);
      dispatch({
        type: ActionType.DELETE_ONTOLOGY,
        payload: {
          uploading: false,
          files: files
        }
      });
    } catch (error) {
      dispatch({
        type: ActionType.DELETE_ONTOLOGY_FAIL,
        payload: {}
      });
      Notification.error({message: error.message});
    }
  }

  async function generateSchema(data: GenerateSchemaModel): Promise<GenerateActionResult> {
    try {
      removeGenerationWarnings();
      toggleRequestIsRunningFlag(true);
      const res: Schema = await OntologyService.generateSchema(data);
      const warnings = res.messages?.warnings || [];
      dispatch({
        type: ActionType.GENERATE_SCHEMA,
        payload: {
          schema: res.schema,
          schemaId: res.id,
          generationWarnings: warnings,
          isRequestRunning: false
        }
      });
    } catch (error) {
      // Schema generator might return errors even though the schema is actually generated.
      // When such scenario occurs we proceed to the next workflow step where the schema is
      // displayed along with the errors.
      if (error && error['@type'] === 'InvalidSchema' && error.schema) {
        const schemaId = SomlUtils.extractSchemaId(error.schema);
        dispatch({
          type: ActionType.GENERATE_SCHEMA_COMPLETE,
          payload: {
            schema: error.schema,
            schemaId: schemaId,
            isRequestRunning: false
          }
        });
        // generation is successful but there are errors
        reportError(error, ActionType.ADD_ERROR);
        return {success: true, hasError: true};
      }
      // generation failed with errors and there is no schema in the response
      reportError(error, ActionType.GENERATE_SCHEMA_FAIL);
      return {success: false, hasError: true};
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    // generation is successful without errors
    return {success: true, hasError: false};
  }

  async function saveSchema(schema: string) {
    try {
      removeError();
      setSchemaId();
      if (!canCreateSchema(schema, noPermissionMessage('You do not have permissions to create or update schema with id='))) {
        return false;
      }
      toggleRequestIsRunningFlag(true);
      const response: SchemaResponse = await SomlService.saveSchema(schema);
      let savedSchema = response.schema;
      // Can happen if there are warnings during save
      if (!savedSchema) {
        savedSchema = await SomlService.getSchema(state.schemaId);
      }
      dispatch({
        type: ActionType.SAVE_SCHEMA,
        payload: {
          schemaId: savedSchema.id,
          schema: savedSchema.schemaYaml,
          permissions: savedSchema.permissions
        }
      });
    } catch (error) {
      let msg = error.message;
      let skipNotification = false;
      if (error.response && error.response.data) {
        msg = error.response.data;
        if (msg.code === EXISTING_SCHEMA_ID) {
          skipNotification = true;
        }
      }
      reportError(error, ActionType.SAVE_SCHEMA_FAIL, skipNotification);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  async function updateSchema(schemaId: string, schema: string) {
    try {
      removeError();
      if (!canCreateSchema(schema, noPermissionMessage('You do not have permissions to create or update schema with id='))) {
        return false;
      }
      toggleRequestIsRunningFlag(true);
      const result: SchemaResponse = await SomlService.updateSchema(schemaId, schema);
      let updatedSchema = result.schema;
      // Can happen if there are warnings during update
      if (!updatedSchema) {
        updatedSchema = await SomlService.getSchema(schemaId);
      }
      dispatch({
        type: ActionType.UPDATE_SCHEMA,
        payload: {
          schema: updatedSchema.schemaYaml
        }
      });
    } catch (error) {
      reportError(error, ActionType.UPDATE_SCHEMA_FAIL);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  function changeSchema(schema: string) {
    dispatch({
      type: ActionType.CHANGE_SCHEMA,
      payload: {
        schema: schema
      }
    });
  }

  // Activate schema

  async function activateSchema(schemaId: string) {
    try {
      toggleRequestIsRunningFlag(true);
      await SomlService.activateSchema(schemaId, props.configurations);
    } catch (error) {
      reportError(error, ActionType.ACTIVATE_SCHEMA_FAIL);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  function removeGenerationWarnings() {
    dispatch({
      type: ActionType.REMOVE_GENERATION_WARNINGS,
      payload: {
        generationWarnings: null
      }
    });
  }

  function removeError() {
    dispatch({
      type: ActionType.REMOVE_ERROR,
      payload: {
        error: null
      }
    });
  }

  return (
    <CreateSchemaContext.Provider value={{
      error: state.error,
      files: state.files,
      schema: state.schema,
      schemaId: state.schemaId,
      uploading: state.uploading,
      progress: state.progress,
      isRequestRunning: state.isRequestRunning,
      permissions: state.permissions,
      generationWarnings: state.generationWarnings,
      removeGenerationWarnings,
      uploadOntologies,
      deleteOntology,
      generateSchema,
      saveSchema,
      updateSchema,
      changeSchema,
      activateSchema,
      removeError
    }}>
      {props.children}
    </CreateSchemaContext.Provider>
  );
};
